import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useInRouterContext } from 'react-router-dom';
import { ArrowRight, Info } from 'lucide-react';
import { GLOSSARY, GlossaryEntry, GlossaryId, fillTip } from '../content/glossary';

// A small (i) beside a term. The words live in content/glossary.ts, and the popover ends with a link to the
// same word in the Guide.
//   mouse     hover shows it, click pins it open, leaving closes it
//   keyboard  Tab onto it shows it, Enter pins it, Tab again moves into the Guide link, Escape closes it
//   touch     a tap pins it, a tap anywhere else closes it
// It floats above the page (a portal), so a table or card with overflow hidden cannot cut it off, and it
// stays inside the window.

type Mode = 'closed' | 'hover' | 'pinned';

const GAP = 6;
const MARGIN = 8;
const CLOSE_DELAY = 120;

function place(trigger: DOMRect, pop: { w: number; h: number }) {
  const left = Math.min(Math.max(MARGIN, trigger.left + trigger.width / 2 - pop.w / 2), Math.max(MARGIN, window.innerWidth - pop.w - MARGIN));
  const below = trigger.bottom + GAP;
  const above = trigger.top - GAP - pop.h;
  const top = below + pop.h > window.innerHeight - MARGIN && above >= MARGIN ? above : below;
  return { left, top };
}

interface Props {
  id: GlossaryId;
  /** Live values for {name} placeholders in the text. */
  vars?: Record<string, string | number>;
  className?: string;
}

export default function InfoTip({ id, vars, className = 'ml-1.5' }: Props) {
  const entry: GlossaryEntry = GLOSSARY[id];
  const popId = useId();
  const [mode, setMode] = useState<Mode>('closed');
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const linkRef = useRef<HTMLAnchorElement>(null);
  // Set while focus is handed back to the button from the Guide link, so that does not reopen the popover.
  const skipOpen = useRef(false);
  const inRouter = useInRouterContext();
  const open = mode !== 'closed';

  const clearTimer = () => window.clearTimeout(timer.current);
  const showOnHover = () => {
    clearTimer();
    setMode((m) => (m === 'closed' ? 'hover' : m));
  };
  const closeSoon = () => {
    clearTimer();
    timer.current = window.setTimeout(() => setMode((m) => (m === 'hover' ? 'closed' : m)), CLOSE_DELAY);
  };
  useEffect(() => clearTimer, []);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const pop = popRef.current;
    if (trigger && pop) setPos(place(trigger.getBoundingClientRect(), { w: pop.offsetWidth, h: pop.offsetHeight }));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  // Escape closes; a press anywhere else closes it too.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (popRef.current?.contains(document.activeElement)) {
        skipOpen.current = true;
        triggerRef.current?.focus();
      }
      setMode('closed');
    };
    const onPress = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!wrapRef.current?.contains(target) && !popRef.current?.contains(target)) setMode('closed');
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress);
    };
  }, [open]);

  const onFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
    // Only a keyboard arrival opens it; a mouse press is handled by hover and click.
    let keyboard = false;
    try {
      keyboard = e.currentTarget.matches(':focus-visible');
    } catch {
      keyboard = false;
    }
    if (skipOpen.current) {
      skipOpen.current = false;
      return;
    }
    if (keyboard) showOnHover();
  };

  // The popover is at the end of the page, so Tab from the button goes into its link by hand, and Tab or
  // Shift+Tab from the link comes back to the button.
  const onTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && !e.shiftKey && open && linkRef.current) {
      e.preventDefault();
      linkRef.current.focus();
    }
  };
  const onLinkKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    skipOpen.current = true;
    triggerRef.current?.focus();
    if (!e.shiftKey) setMode('closed');
  };
  const guideHref = `/guide#${id}`;
  const linkClass = 'inline-flex items-center gap-1 rounded text-xs font-medium text-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

  const onBlur = (e: React.FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (next && (wrapRef.current?.contains(next) || popRef.current?.contains(next))) return;
    setMode('closed');
  };

  return (
    <span ref={wrapRef} role="presentation" className={`inline-flex align-middle ${className}`} onMouseEnter={showOnHover} onMouseLeave={closeSoon} onBlur={onBlur} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`About ${entry.term}`}
        aria-expanded={open}
        aria-describedby={open ? popId : undefined}
        onClick={() => setMode((m) => (m === 'pinned' ? 'closed' : 'pinned'))}
        onFocus={onFocus}
        onKeyDown={onTriggerKey}
        className="-m-1 rounded-full p-1 text-gray-500 transition-colors hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
      >
        <Info className="h-4 w-4" aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            id={popId}
            role="dialog"
            aria-label={entry.term}
            aria-modal="false"
            tabIndex={-1}
            style={{ position: 'fixed', left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden' }}
            className="z-[60] w-72 max-w-[calc(100vw-1rem)] rounded-xl bg-white px-4 py-3 text-left normal-case tracking-normal shadow-lg ring-1 ring-gray-200 focus:outline-none contrast-more:ring-gray-500"
          >
            <p className="text-[0.8125rem] font-semibold text-gray-900">{entry.term}</p>
            <p className="mt-1 text-sm font-normal leading-relaxed text-gray-600">{fillTip(entry.short, vars)}</p>
            {entry.example && <p className="mt-2 text-xs font-normal leading-relaxed text-gray-500">Example: {entry.example}</p>}
            <p className="mt-2 font-normal">
              {inRouter ? (
                <Link ref={linkRef} to={guideHref} onClick={() => setMode('closed')} onKeyDown={onLinkKey} className={linkClass}>
                  More in the Guide <ArrowRight className="h-3 w-3" aria-hidden />
                </Link>
              ) : (
                <a ref={linkRef} href={guideHref} onKeyDown={onLinkKey} className={linkClass}>
                  More in the Guide <ArrowRight className="h-3 w-3" aria-hidden />
                </a>
              )}
            </p>
          </div>,
          document.body
        )}
    </span>
  );
}
